import { useCallback, useEffect, useState, useSyncExternalStore, type ReactElement } from 'react';

import { Button } from '~/components/ui/Button';
import { Container } from '~/components/ui/Container';
import { useContent, useLocale } from '~/hooks/useContent';
import { localePrivacyPath } from '~/i18n/config';
import { cn } from '~/lib/cn';
import { updateConsent } from '~/lib/consentMode';

/**
 * The localStorage key the consent banner reads/writes. The same key is
 * inspected by GtmLoader to decide whether to inject Google Tag Manager.
 */
export const CONSENT_STORAGE_KEY = 'arpa:consent';
export const CONSENT_GRANTED_EVENT = 'arpa:consent-granted';

export type ConsentValue = 'granted' | 'denied';

function readStoredConsent(): ConsentValue | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === 'granted' || value === 'denied') return value;
    return null;
  } catch {
    // localStorage can throw in private mode / disabled storage; treat as
    // "no decision" so we keep showing the banner without crashing.
    return null;
  }
}

/**
 * Subscribe to cross-tab and intra-tab consent changes. The browser fires
 * `storage` events on _other_ tabs only, so we also listen for the same
 * custom event GtmLoader uses (and which we dispatch on accept) to keep
 * the local UI in sync.
 */
function subscribeToConsent(notify: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key === CONSENT_STORAGE_KEY) notify();
  };
  const onLocal = (): void => {
    notify();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CONSENT_GRANTED_EVENT, onLocal);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CONSENT_GRANTED_EVENT, onLocal);
  };
}

/**
 * Snapshot used during SSR and the first hydration pass. Returning the
 * "no decision yet" placeholder for both server and client guarantees the
 * server-rendered output (banner hidden because we conservatively treat
 * the snapshot as already-decided in `useConsentVisibility` below) matches
 * the client's first render — the banner only appears after a real client
 * snapshot is read in the next render cycle.
 */
function getServerSnapshot(): ConsentValue | null {
  return 'granted';
}

/**
 * True when the consent banner should be visible — i.e. the user has not
 * yet made a choice. Backed by `useSyncExternalStore` so it stays in sync
 * across tabs and across the in-app accept event without triggering a
 * setState-in-effect lint warning.
 */
function useConsentVisibility(): boolean {
  const consent = useSyncExternalStore(subscribeToConsent, readStoredConsent, getServerSnapshot);
  return consent === null;
}

/**
 * Minimal GDPR cookie banner. The server renders nothing because the
 * snapshot used during SSR pretends consent was already granted; once the
 * client reads localStorage the banner appears if no choice is stored.
 *
 *   - "Accept" persists `arpa:consent=granted` and dispatches a custom
 *     `arpa:consent-granted` event so GtmLoader can react without a
 *     full reload.
 *   - "Reject" persists `arpa:consent=denied` and hides the banner.
 *
 * The banner is rendered as a non-modal dialog (`aria-modal="false"`) so
 * keyboard focus is not trapped — the user can keep navigating the page.
 */
export function CookieConsent(): ReactElement | null {
  const content = useContent();
  const locale = useLocale();
  const { cookies } = content;
  const privacyHref = localePrivacyPath(locale);

  const visible = useConsentVisibility();
  // Local override so the banner can disappear immediately on click before
  // the storage subscription fires.
  const [dismissed, setDismissed] = useState(false);

  const persist = useCallback((value: ConsentValue): void => {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
    } catch {
      // No-op; banner still hides so the user isn't trapped.
    }
  }, []);

  // Reapply any previously-stored consent signal on mount so Google Consent
  // Mode v2 sees the returning user's decision. The inline <head> script
  // initialises every signal to "denied"; without this effect a user who
  // accepted on a prior visit would momentarily show as denied until they
  // interact with the banner again (which they never will — the banner is
  // hidden because localStorage already has a value).
  useEffect(() => {
    const stored = readStoredConsent();
    if (stored !== null) {
      updateConsent(stored);
    }
  }, []);

  const onAccept = useCallback((): void => {
    // Fire the GCM signal BEFORE persisting so any listener that reads
    // localStorage off the custom event sees a consistent snapshot.
    updateConsent('granted');
    persist('granted');
    window.dispatchEvent(new Event(CONSENT_GRANTED_EVENT));
    setDismissed(true);
  }, [persist]);

  const onReject = useCallback((): void => {
    updateConsent('denied');
    persist('denied');
    setDismissed(true);
  }, [persist]);

  if (!visible || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-heading"
      aria-modal="false"
      className={cn(
        'fixed inset-x-0 bottom-0 z-[90]',
        'bg-[var(--color-deep-panel)] text-[var(--color-frost-white)]',
        'border-t-2 border-[var(--color-cyan-signal)]',
        'shadow-[0_-12px_32px_-12px_rgba(0,0,0,0.6)]'
      )}
    >
      <Container className="py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
          <div className="flex-1 min-w-0">
            <p
              id="cookie-heading"
              className={cn(
                'font-display font-semibold text-sm tracking-[0.18em] uppercase',
                'text-[var(--color-cyan-signal)] mb-1'
              )}
            >
              {cookies.heading}
            </p>
            <p className="text-sm font-body leading-relaxed text-[var(--color-frost-white)]/85">
              {cookies.body}{' '}
              <a
                href={privacyHref}
                className={cn(
                  'underline underline-offset-2 transition-colors',
                  'hover:text-[var(--color-cyan-signal)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm'
                )}
              >
                {cookies.policyLink}
              </a>
            </p>
          </div>

          <div className="flex flex-row gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={onReject}>
              {cookies.reject}
            </Button>
            <Button variant="primary" size="sm" onClick={onAccept}>
              {cookies.accept}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default CookieConsent;
